import { useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/auth";
import Logo from "@/components/Logo";

/**
 * Landing page for the WorkOS email-verification callback.
 *
 * The backend `/auth/verify-email?code=...` endpoint verifies the code,
 * creates a session, and redirects here with either `?success=1` or
 * `?error=<code>`. If the session was created, `/auth/me` will now return
 * the user — we call `refresh()` so the app context picks it up without a
 * reload, then redirect to `/onboarding`.
 */
export default function EmailVerifiedPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const success = searchParams.get("success") === "1";
  const error = searchParams.get("error");

  useEffect(() => {
    if (!success) return;
    void refresh().then(() => {
      navigate("/onboarding", { replace: true });
    });
  }, [success, refresh, navigate]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-white px-8">
      <Link to="/" className="mb-8">
        <Logo />
      </Link>

      {success ? (
        <div className="text-center" data-testid="email-verified-success">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
            <svg className="h-6 w-6 text-green-600" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h1 className="mt-4 font-display text-2xl font-bold text-brand-primary">
            {t("emailVerified.successTitle")}
          </h1>
          <p className="mt-2 text-sm text-neutral-600">
            {t("emailVerified.successBody")}
          </p>
        </div>
      ) : (
        <div className="text-center" data-testid="email-verified-error">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <svg className="h-6 w-6 text-red-600" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M6 18L18 6M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h1 className="mt-4 font-display text-2xl font-bold text-brand-primary">
            {t("emailVerified.errorTitle")}
          </h1>
          <p className="mt-2 text-sm text-neutral-600">
            {error === "missing_code" || error === "invalid_code"
              ? t("emailVerified.errorBody")
              : t("emailVerified.errorBody")}
          </p>
          <Link
            to="/login"
            className="mt-6 inline-block rounded-lg bg-brand-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90"
          >
            {t("emailVerified.loginCta")}
          </Link>
        </div>
      )}
    </div>
  );
}
